import axios, { AxiosInstance } from 'axios';
import { AdvisorInfo, AdvisorFeatures, OutputType } from '../types/advisor';

const API_BASE_URL = import.meta.env.VITE_PLANSCOUT_API_URL || 'http://localhost:8080';

/**
 * Create axios instance with authorization header
 * @param token - Auth0 access token
 */
const createAuthorizedClient = (token: string): AxiosInstance => {
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export interface AdvisorConfig extends AdvisorInfo {
  _id?: string;
  advisorPhotoBlobName?: string;
  advisorPhotoUrlExpiry?: Date;
  companyLogoBlobName?: string;
  companyLogoUrlExpiry?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  lastEditedBy?: string;
}

/**
 * Fetch all advisor configs (filtered by user permissions on backend)
 * @param token - Auth0 access token
 */
export const getAllAdvisorConfigs = async (token: string): Promise<AdvisorConfig[]> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get('/api/advisor-configs');
    return response.data;
  } catch (error) {
    console.error('Error fetching all advisor configs:', error);
    throw error;
  }
};

/**
 * Fetch specific advisor config by customer ID
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 */
export const getAdvisorConfig = async (token: string, customerId: string): Promise<AdvisorConfig> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.get(`/api/advisor-configs/${customerId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching advisor config for ${customerId}:`, error);
    throw error;
  }
};

/**
 * Create new advisor config (admin only)
 * @param token - Auth0 access token
 * @param config - Advisor configuration data
 */
export const createAdvisorConfig = async (
  token: string,
  config: Partial<AdvisorConfig> & { provisionSips?: boolean; generateCustomerId?: boolean }
): Promise<AdvisorConfig & { provisioning?: { status: string; sipsTasId?: string; error?: string } }> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.post('/api/advisor-configs', config);
    return response.data;
  } catch (error) {
    console.error('Error creating advisor config:', error);
    throw error;
  }
};

/**
 * Update advisor config (role-based permissions)
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 * @param updates - Fields to update
 */
export const updateAdvisorConfig = async (
  token: string,
  customerId: string,
  updates: Partial<AdvisorConfig>
): Promise<AdvisorConfig> => {
  try {
    const client = createAuthorizedClient(token);
    const response = await client.put(`/api/advisor-configs/${customerId}`, updates);
    return response.data;
  } catch (error) {
    console.error(`Error updating advisor config for ${customerId}:`, error);
    throw error;
  }
};

/**
 * Delete advisor config (admin only)
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 */
export const deleteAdvisorConfig = async (token: string, customerId: string): Promise<void> => {
  try {
    const client = createAuthorizedClient(token);
    await client.delete(`/api/advisor-configs/${customerId}`);
  } catch (error) {
    console.error(`Error deleting advisor config for ${customerId}:`, error);
    throw error;
  }
};

/**
 * Upload advisor photo to Azure Blob Storage
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 * @param file - Image file
 */
export const uploadAdvisorPhoto = async (
  token: string,
  customerId: string,
  file: File
): Promise<{ advisorPhotoUrl: string; urlExpiry: Date }> => {
  try {
    const formData = new FormData();
    formData.append('photo', file);

    const response = await axios.post(
      `${API_BASE_URL}/api/advisor-configs/${customerId}/upload-photo`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading advisor photo:', error);
    throw error;
  }
};

/**
 * Upload company logo to Azure Blob Storage
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 * @param file - Image file
 */
export const uploadCompanyLogo = async (
  token: string,
  customerId: string,
  file: File
): Promise<{ companyLogoUrl: string; urlExpiry: Date }> => {
  try {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await axios.post(
      `${API_BASE_URL}/api/advisor-configs/${customerId}/upload-logo`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading company logo:', error);
    throw error;
  }
};

// ==================== Partner Webhook (plan.completed callback) ====================

export type WebhookAuthScheme = 'bearer' | 'hmac' | 'oauth2';
export type WebhookIncludePart = 'prompt' | 'metrics' | 'artifacts';

export interface PartnerWebhookOAuth2Config {
  tokenUrl?: string;
  clientId?: string;
  clientSecretIsSet?: boolean;
}

export interface PartnerWebhookConfig {
  enabled?: boolean;
  environment?: 'prod' | 'qa';
  url?: string;
  authScheme?: WebhookAuthScheme;
  includeParts?: WebhookIncludePart[];
  outputs?: OutputType[];
  promptTemplateId?: string | null;
  retryPolicy?: { maxAttempts?: number; backoffSeconds?: number[] };
  secretIsSet?: boolean;
  oauth2?: PartnerWebhookOAuth2Config;
  lastDelivery?: { status: 'success' | 'failed'; at: string; attempts: number };
}

export interface PartnerWebhookOAuth2Update {
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string; // plaintext; server encrypts. Omit to keep existing.
}

export interface PartnerWebhookUpdate extends Omit<PartnerWebhookConfig, 'secretIsSet' | 'lastDelivery' | 'oauth2'> {
  secret?: string; // plaintext; server encrypts. Omit to keep existing.
  oauth2?: PartnerWebhookOAuth2Update;
}

export interface WebhookTestResult {
  success: boolean;
  delivery: {
    id: string;
    status: string;
    attemptCount: number;
    lastAttempt: { statusCode?: number | null; errorMessage?: string | null; durationMs?: number } | null;
  };
}

// Partner webhook is now firm/branch-scoped — the config methods live in firmService
// (getWebhookConfig/updateWebhookConfig/sendWebhookTest). The types above are shared.

// ==================== Prompt Templates (used by partner webhook) ====================

export interface PromptTemplateInsight {
  key: string;
  requireExpr?: string;
  block?: string;
}

export interface PromptTemplate {
  _id: string;
  name: string;
  description?: string;
  bodyTemplate: string;
  insights: PromptTemplateInsight[];
  version: number;
  createdAt?: string;
  updatedAt?: string;
  lastEditedBy?: string;
}

export const listPromptTemplates = async (token: string): Promise<PromptTemplate[]> => {
  const client = createAuthorizedClient(token);
  const response = await client.get('/api/prompt-templates');
  return response.data.data;
};

export const createPromptTemplate = async (
  token: string,
  data: Pick<PromptTemplate, 'name' | 'description' | 'bodyTemplate' | 'insights'>
): Promise<PromptTemplate> => {
  const client = createAuthorizedClient(token);
  const response = await client.post('/api/prompt-templates', data);
  return response.data;
};

export const updatePromptTemplate = async (
  token: string,
  id: string,
  data: Partial<Pick<PromptTemplate, 'name' | 'description' | 'bodyTemplate' | 'insights'>>
): Promise<PromptTemplate> => {
  const client = createAuthorizedClient(token);
  const response = await client.put(`/api/prompt-templates/${id}`, data);
  return response.data;
};

export const deletePromptTemplate = async (token: string, id: string): Promise<void> => {
  const client = createAuthorizedClient(token);
  await client.delete(`/api/prompt-templates/${id}`);
};

// ==================== Create SIPS accounts (superadmin) ====================

export type CreateSipsAccountOutcome = 'created' | 'failed' | 'not-found';

export interface CreateSipsAccountResult {
  customerId: string;
  name: string | null;
  previousSipsTasId: string | null;
  sipsTasId: string | null;
  outcome: CreateSipsAccountOutcome;
  reason: string;
}

export interface CreateSipsAccountsResponse {
  success: boolean;
  counts: Partial<Record<CreateSipsAccountOutcome, number>>;
  results: CreateSipsAccountResult[];
}

/**
 * Create a brand-new SIPS customer for each listed advisor and point the advisor
 * at it (superadmin only).
 *
 * Always creates — SIPS's create endpoint is not idempotent and its API can't
 * reveal who an existing customer id belongs to, so choosing which advisors need
 * an account is the operator's call. Max 25 per request; callers chunk.
 */
export const createSipsAccounts = async (
  token: string,
  options: { customerIds: string[]; seedPrefs?: boolean }
): Promise<CreateSipsAccountsResponse> => {
  const client = createAuthorizedClient(token);
  const response = await client.post('/api/advisor-configs/create-sips-accounts', {
    customerIds: options.customerIds,
    seedPrefs: options.seedPrefs !== false,
  });
  return response.data;
};

// ==================== Cache Management ====================

interface CacheEntry {
  data: AdvisorConfig[];
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let configCache: CacheEntry | null = null;

/**
 * Get all advisor configs with caching
 * @param token - Auth0 access token
 * @param forceRefresh - Force cache refresh
 */
export const getCachedAdvisorConfigs = async (
  token: string,
  forceRefresh: boolean = false
): Promise<AdvisorConfig[]> => {
  const now = Date.now();

  // Return cached data if valid and not forcing refresh
  if (!forceRefresh && configCache && (now - configCache.timestamp) < CACHE_DURATION) {
    console.log('📦 Returning cached advisor configs');
    return configCache.data;
  }

  // Fetch fresh data
  console.log('🔄 Fetching fresh advisor configs');
  const data = await getAllAdvisorConfigs(token);

  // Update cache
  configCache = {
    data,
    timestamp: now
  };

  return data;
};

/**
 * Merge a partial update into one cached config in place (no refetch).
 * Used for small self-service writes (e.g. dashboard layout prefs) where a full
 * cache invalidation would make every book-level page refetch configs.
 */
export const patchCachedAdvisorConfig = (
  customerId: string,
  patch: Partial<AdvisorConfig>
): void => {
  if (!configCache) return;
  configCache = {
    ...configCache,
    data: configCache.data.map((c) => (c.customerId === customerId ? { ...c, ...patch } : c)),
  };
};

/**
 * Invalidate the advisor config cache
 * Call this after updating any advisor config
 */
export const invalidateAdvisorConfigCache = (): void => {
  console.log('🗑️  Invalidated advisor config cache');
  configCache = null;
};

/**
 * Get single advisor config by customer ID with caching
 * @param token - Auth0 access token
 * @param customerId - Customer ID
 */
export const getCachedAdvisorConfig = async (
  token: string,
  customerId: string
): Promise<AdvisorConfig | null> => {
  // Try to get from cache first
  if (configCache && (Date.now() - configCache.timestamp) < CACHE_DURATION) {
    const cached = configCache.data.find(config => config.customerId === customerId);
    if (cached) {
      console.log(`📦 Returning cached advisor config for ${customerId}`);
      return cached;
    }
  }

  // Fetch from API
  try {
    console.log(`🔄 Fetching advisor config for ${customerId}`);
    return await getAdvisorConfig(token, customerId);
  } catch (error) {
    console.error(`Failed to fetch advisor config for ${customerId}:`, error);
    return null;
  }
};
